import type { V1Number } from '@/generated/hooks/grimoire/models';

/**
 * Encodes a numeric string value to a V1Number format.
 *
 * The V1Number format represents fixed-precision decimal numbers where:
 * - The value is calculated as: m / 10^e
 * - m is an unsigned 96-bit integer composed of (lo, mid, hi)
 * - e is the scale (0–28) stored in flags bits 16–23
 * - sign is encoded in flags bit 31 (0 = positive, 1 = negative)
 *
 * This function reverses the decoding logic from src/modules/outputs/utils.ts
 *
 * @param value - The numeric string to encode
 * @returns The encoded V1Number object
 */
export function encodeNumber(value: string): V1Number {
  // Parse the input value
  const numValue = parseFloat(value);

  // Handle special cases
  if (isNaN(numValue) || !isFinite(numValue)) {
    return { lo: 0, mid: 0, hi: 0, flags: 0 };
  }

  // Determine if negative
  const isNegative = numValue < 0;
  const absValue = Math.abs(numValue);

  // Determine the scale (number of decimal places)
  // We need to count decimal places in the original string to preserve precision
  let scale = 0;
  const valueStr = value.trim();
  const decimalIndex = valueStr.indexOf('.');

  if (decimalIndex !== -1) {
    // Count digits after decimal point
    const afterDecimal = valueStr.substring(decimalIndex + 1);
    // Remove any trailing zeros and non-digit characters (like 'e' in scientific notation)
    const digitsOnly = afterDecimal.replace(/[^0-9]/g, '');
    scale = digitsOnly.length;

    // Cap scale at 28 as per V1Number spec
    if (scale > 28) {
      scale = 28;
    }
  }

  // Calculate the mantissa: m = value * 10^scale
  // This gives us the integer representation
  const mantissa = Math.round(absValue * Math.pow(10, scale));

  // Break down mantissa into 32-bit components
  // JavaScript bitwise operations work on 32-bit signed integers,
  // so we need to be careful with the conversion
  const lo = mantissa & 0xffffffff;
  const mid = Math.floor(mantissa / Math.pow(2, 32)) & 0xffffffff;
  const hi = Math.floor(mantissa / Math.pow(2, 64)) & 0xffffffff;

  // Construct flags: scale in bits 16-23, sign in bit 31
  let flags = (scale & 0xff) << 16;
  if (isNegative) {
    flags = (flags | 0x80000000) >>> 0;
  }

  return {
    lo,
    mid,
    hi,
    flags,
  };
}
